import { useEffect, useId, useRef, useState } from "react";
import {
  FAIR_PRICE_ANALYTICS_EVENTS,
  FAIR_PRICE_DISCLAIMER,
  FAIR_PRICE_METHODOLOGY_ES,
} from "../../constants/automapFairPriceConstants";
import { getFairPriceViewModel } from "../../utils/fairPriceViewModel";
import { buildFairPriceAnalyticsPayload, trackFairPriceEvent } from "../../utils/fairPriceAnalytics";
import { useOptionalLocale } from "../../intl";
import "../../styles/fairPriceBadge.css";

export default function FairPriceBadge({
  listing,
  fairPriceLabel,
  size = "medium",
  variant = "default",
  showTooltip = true,
  showLabel = true,
  useShortLabel = false,
  context = "default",
  hideIfNoData = true,
  className = "",
  onBadgeClick,
}) {
  const tooltipId = useId();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const viewedRef = useRef(false);
  const localeContext = useOptionalLocale();

  const subject = listing || (fairPriceLabel ? { fairPriceLabel } : null);
  const vm = getFairPriceViewModel(subject, {
    t: localeContext?.t,
    locale: localeContext?.locale,
  });
  const moreInfoLabel = localeContext?.t
    ? localeContext.t("automotive.badges.fairPriceMoreInfo", { label: vm.displayLabel })
    : `Más información: ${vm.displayLabel}`;

  useEffect(() => {
    if (!subject || viewedRef.current) return;
    if (hideIfNoData && !vm.showBadge) return;
    viewedRef.current = true;
    trackFairPriceEvent(FAIR_PRICE_ANALYTICS_EVENTS.BADGE_VIEWED, buildFairPriceAnalyticsPayload(subject, {
      context,
      variant,
      sourceComponent: "fair_price_badge",
    }));
  }, [subject?.id, vm.showBadge, hideIfNoData, context, variant]);

  if (hideIfNoData && !vm.showBadge) {
    return null;
  }

  const labelText = useShortLabel || variant === "card" ? vm.shortLabel : vm.displayLabel;

  const classes = [
    "fair-price-badge",
    `fair-price-badge--${size}`,
    `fair-price-badge--${variant}`,
    `fair-price-badge--${vm.badgeTone}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <span className={classes}>
      <span
        className="fair-price-badge__pill"
        aria-label={vm.displayLabel}
        title={!tooltipOpen ? vm.description : undefined}
        aria-describedby={tooltipOpen ? tooltipId : undefined}
        onClick={onBadgeClick}
        onKeyDown={onBadgeClick ? (e) => e.key === "Enter" && onBadgeClick(e) : undefined}
        role={onBadgeClick ? "button" : undefined}
        tabIndex={onBadgeClick ? 0 : undefined}
      >
        {showLabel ? <span className="fair-price-badge__label">{labelText}</span> : null}
        {showTooltip ? (
          <button
            type="button"
            className="fair-price-badge__info"
            aria-label={moreInfoLabel}
            aria-expanded={tooltipOpen}
            aria-controls={tooltipId}
            onClick={(event) => {
              event.stopPropagation();
              setTooltipOpen((open) => {
                if (!open) {
                  trackFairPriceEvent(
                    FAIR_PRICE_ANALYTICS_EVENTS.TOOLTIP_OPENED,
                    buildFairPriceAnalyticsPayload(subject, { context, label: vm.label })
                  );
                }
                return !open;
              });
            }}
          >
            i
          </button>
        ) : null}
      </span>
      {tooltipOpen ? (
        <span id={tooltipId} role="tooltip" className="fair-price-badge__tooltip">
          {vm.description}
          {vm.sampleLabel ? ` ${vm.sampleLabel}.` : null}
          <span className="fair-price-badge__disclaimer">{FAIR_PRICE_METHODOLOGY_ES}</span>
          <span className="fair-price-badge__disclaimer">{FAIR_PRICE_DISCLAIMER}</span>
        </span>
      ) : null}
    </span>
  );
}
